import * as React from 'react';
import styled from 'styled-components';
import { Body1 } from "--components/typography";
import IcoUpload from '@floji-internal/mui-icons/CloudUpload';
import { CSSTransition } from 'react-transition-group';
import Color from 'color';


const STRINGS = {
    attachFile: "Attach file to message",
}

type PositionArgs = { x: number, y: number, w: number, h: number };


type DropTargetProps = {
    disable?: boolean
    showDropZone?: boolean
    containerRef?: React.RefObject<HTMLElement>     // Container to position relative to
    attachFile: (file: File, idx: number) => void
    style?: React.CSSProperties
    className?: string
}

const DropArea = styled.div<{ position?: PositionArgs }>`
    position: fixed;
    box-sizing: border-box;
    
    top: ${props => props.position?.y || 0}px;
    left: ${props => props.position?.x || 0}px;
    width: ${props => props.position?.w || window.innerWidth}px;
    height: ${props => props.position?.h || window.innerWidth}px;

    padding: ${props => props.theme.spacing(1)};
    background: ${props => Color(props.theme.palette.background).alpha(0.9).toString()};
    transition: opacity ${props => props.theme.transitions.standard};
    justify-content: center;
    align-items: center;
    z-index: ${({ theme }) => theme.zIndex.modal};

    & div {
        flex: 0 0 auto;
        text-align: center;
        pointer-events: none;
    }

    & svg {
        color: ${props => props.theme.palette.primary.main};
    }

    /* Animation */
    display: none;
    opacity: 0;

	&.fade-appear,
    &.fade-enter {
        opacity: 0;
        display: flex;
    }
    &.fade-appear-active,
    &.fade-enter-active,
    &.fade-appear-done,
    &.fade-enter-done {
        opacity: 1;
        display: flex;
        transition: opacity ${props => props.theme.transitions.standard};
    }
    &.fade-exit {
        opacity: 1;
        display: flex;
    }
    &.fade-exit-active {
        opacity: 0;
        display: flex;
        transition: opacity ${props => props.theme.transitions.short};
    }
    &.fade-exit-done {
        opacity: 0;
        display: none;
    }
`



const DropTarget: React.FC<DropTargetProps> = (props) => {
    const { attachFile, showDropZone, children, disable, containerRef, ...containerProps } = props;
    const dropRef = React.useRef<HTMLDivElement>(null);
    const [hovering, setHovering] = React.useState<boolean>(false);
    const [position, setPosition] = React.useState<PositionArgs>();
    const dragCounter = React.useRef(0);

    React.useEffect(() => {
        if (disable) {
            removeListeners();
        } else {
            addListeners();
            setHovering(false);
        }
        return removeListeners;
    }, [disable]);

    const addListeners = () => {
        document.body.addEventListener('dragenter', onDragEnter);
        document.body.addEventListener('dragover', onDragOver);
        document.body.addEventListener('dragleave', onDragLeave);
        document.body.addEventListener('drop', onDrop);
    }
    const removeListeners = () => {
        document.body.removeEventListener('dragenter', onDragEnter);
        document.body.removeEventListener('dragover', onDragOver);
        document.body.removeEventListener('dragleave', onDragLeave);
        document.body.removeEventListener('drop', onDrop);
    }
    const onDragEnter = (e: DragEvent) => {
        e.preventDefault();
        dragCounter.current++;
        if (dragCounter.current == 1) {
            const container = containerRef && containerRef.current || document.body;
            setPosition({ x: container.offsetLeft, y: container.offsetTop, w: container.offsetWidth, h: container.offsetHeight });
            setHovering(true);
        }
    }
    const onDragOver = (e: DragEvent) => {
        e.preventDefault();
    }
    const onDragLeave = (e: DragEvent) => {
        e.preventDefault();
        dragCounter.current--;
        if (dragCounter.current == 0) {
            setHovering(false);
        }
    }
    const onDrop = (e: DragEvent) => {
        e.preventDefault();
        setHovering(false);
        dragCounter.current = 0;
        if (e.target !== dropRef.current) return;
        const fileList = e.dataTransfer?.files;
        if (!fileList) return;
        Array.from(fileList).forEach((file, idx) => attachFile(file, idx));
    }

    const transitionEnd = (elem: HTMLElement, cb: () => void) => {
        elem.addEventListener('transitionend', cb, { once: true });
    }


    return <>
        <CSSTransition in={hovering || showDropZone || false} appear={true} classNames="fade" addEndListener={transitionEnd}>
            <DropArea ref={dropRef} position={position} {...containerProps}>
                <div>
                    <IcoUpload size="64px" />
                    <Body1 margin>{STRINGS.attachFile}</Body1>
                </div>
            </DropArea>
        </CSSTransition>
    </>
}



export default DropTarget;
export { DropTarget, DropTargetProps };